import React from "react";
import { Phone, MapPin, Tag } from "lucide-react";
import { SITE } from "../../site.config";
import QuoteButton from "./QuoteButton";
import PromoCard from "./PromoCard";

export default function Hero() {
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden bg-black"
    >
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${SITE.hero.image})` }}
        aria-hidden="true"
      />
      {/* Diagonal accent line */}
      <div
        className="absolute inset-y-0 right-0 w-1/3 diag-line opacity-40 hidden md:block"
        aria-hidden="true"
      />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-8 pt-40 pb-24 md:pt-48 md:pb-32 min-h-[100svh] flex flex-col justify-center">
        <div className="flex items-center gap-3 mb-6">
          <span className="h-px w-10 bg-cyan-pz" />
          <span className="bebas text-cyan-pz tracking-[0.3em] text-sm md:text-base text-stroke">
            {SITE.hero.eyebrow}
          </span>
        </div>

        <h1
          data-testid="hero-headline"
          className="bebas text-white text-[13vw] md:text-[7.5vw] lg:text-[6.5rem] xl:text-[7.5rem] leading-[0.9] tracking-tight max-w-5xl text-stroke-lg"
        >
          {SITE.hero.headline.split("\n").map((line, i) => (
            <span key={i} className="block">
              {i === 1 ? (
                <span className="text-cyan-pz glow-cyan">{line}</span>
              ) : (
                line
              )}
            </span>
          ))}
          {SITE.hero.headlineTail && (
            <span
              data-testid="hero-headline-tail"
              className="block mt-3 text-white/85 text-[5.5vw] md:text-[2.6vw] lg:text-[2.1rem] xl:text-[2.4rem] leading-tight tracking-[0.08em] uppercase"
            >
              {SITE.hero.headlineTail}
            </span>
          )}
        </h1>

        {SITE.hero.slogan && (
          <p
            data-testid="hero-slogan"
            className="mt-4 bebas text-cyan-pz/80 tracking-[0.35em] text-sm md:text-base uppercase"
          >
            {SITE.hero.slogan}
          </p>
        )}

        <p
          data-testid="hero-sub"
          className="mt-8 text-base md:text-xl text-white max-w-2xl barlow leading-relaxed text-stroke"
        >
          {SITE.hero.sub}
        </p>

        <div className="mt-10 flex flex-col sm:flex-row gap-4">
          <a
            href={`tel:${SITE.business.phoneTel}`}
            data-testid="hero-call-btn"
            className="inline-flex items-center justify-center gap-3 bg-cyan-pz text-black px-8 py-5 bebas text-xl tracking-[0.2em] hover:bg-white transition-colors clip-perf pulse-cyan"
          >
            <Phone className="w-5 h-5" />
            CALL {SITE.business.phone}
          </a>
          <QuoteButton variant="outline" testId="hero-quote-btn" />
          <a
            href="#pricing"
            data-testid="hero-pricing-btn"
            className="inline-flex items-center justify-center gap-3 border border-cyan-pz/60 text-cyan-pz px-8 py-5 bebas text-xl tracking-[0.2em] hover:bg-cyan-pz hover:text-black transition-colors"
          >
            <Tag className="w-5 h-5" />
            VIEW PRICING
          </a>
        </div>

        <PromoCard />

        <div className="mt-14 flex flex-wrap items-center gap-x-8 gap-y-3 text-white text-sm">
          <span className="flex items-center gap-2 text-stroke">
            <MapPin className="w-4 h-4 text-cyan-pz" />
            {SITE.business.address.full}
          </span>
          <span className="hidden md:inline-block w-px h-4 bg-white/40" />
          <span className="bebas tracking-widest text-stroke">
            {SITE.business.serviceArea}
          </span>
        </div>
      </div>

      {/* Bottom marquee strip */}
      <div className="absolute bottom-0 left-0 right-0 z-10 overflow-hidden border-y border-white/10 bg-black/80 backdrop-blur-sm">
        <div className="flex animate-marquee whitespace-nowrap">
          {[...Array(2)].map((_, j) => (
            <div key={j} className="flex items-center">
              {[
                "TOWING ELECTRICS",
                "REDARC TOW-PRO INSTALLS",
                "12 PIN TRAILER PLUGS",
                "ANDERSON PLUGS",
                "AUTO ELECTRICIAN",
                "4X4 AUTO ELECTRICAL",
                "UNDER-BONNET DUAL BATTERY",
                "AUTO AIR CONDITIONING",
                "SUNSHINE COAST",
              ].map((t, i) => (
                <span
                  key={`${j}-${i}`}
                  className="bebas text-white/50 text-base md:text-lg tracking-[0.3em] px-8 py-4 flex items-center gap-8"
                >
                  {t}
                  <span className="text-cyan-pz">●</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
